import {
  InputMappingConstants,
  MappingSuffixes,
  OutputMappingConstants,
} from 'components/property-editors/MappingConstants';
import {
  ContextCategoryNode,
  ContextProviderNode,
  isContextCategoryNode,
} from '@decisions/server/DecisionsFramework/Design/Properties';
import { getMappingName } from 'components/property-editors/MappingTypeSelector/utils/MappingSelectorUtils';
import {
  categoryIsMappingTypeSelector,
  findContextChildInListByPath,
  getMappingDataType,
  IProviderNodeSearchCondition,
  NodeTypes,
  PROVIDER_NODE_PATH_SEPARATOR,
  providerNodeSearch,
  IPropertyChild,
} from 'components/property-editors/utils/ProviderNodeUtils';
import { DataDescription } from '@decisions/server/DecisionsFramework/Design/Flow/Mapping';
import { IMapping, IMappingRowDimensions, MappingPanelFilter, NestedDataDesc } from '../MappingEditorStore';
import {
  getAllValidationIssues,
  getParentValidationSummary,
  NodeValidationIssues,
  ValidationSummary,
} from 'utils/ValidationUtils';
import { BreakLevel } from '@decisions/server/DecisionsFramework';
import { EditorNames } from 'components/Editors/EditorNames';
import { IActionLinkItem, IActionLinkItems } from 'components/ActionLinks/ActionLinkModels';
import { DataDescriptionType } from '@decisions/server/DecisionsFramework/Utilities/Data';
import { debounce } from '@material-ui/core';

export const DATA_PATH_SEPARATOR = '.';
export const MAPPING_CATEGORY_PREFIX = 'Category|~|';

export function mappingTypeRendersInlineEditor(mappingType: string) {
  switch (mappingType) {
    case InputMappingConstants.MAPPING_CONSTANT:
    case InputMappingConstants.MAPPING_DATE:
    case InputMappingConstants.MAPPING_DATETIME:
    case InputMappingConstants.MAPPING_MERGE_HTML:
    case InputMappingConstants.MAPPING_MERGE_PLAIN:
    case OutputMappingConstants.MAPPING_RENAME:
    case OutputMappingConstants.MAPPING_REFERENCE:
      return true;
    default:
      return false;
  }
}

export function mappingTypeRendersInOverlay(mappingType: string) {
  switch (mappingType) {
    // Build data DOES show in an overlay if it's an editable build data, otherwise not.
    // Because it's conditions are checked otherwise, we're not returning true for it here.
    // case InputMappingConstants.MAPPING_BUILD:
    case InputMappingConstants.MAPPING_RUN_CONVERTER_FLOW:
    case InputMappingConstants.MAPPING_DATETIME_COMPUTE:
    case OutputMappingConstants.MAPPING_RUN_CONVERTER_FLOW:
      return true;
    default:
      return false;
  }
}

export function mappingTypeHasChildren(mappingType: string) {
  switch (mappingType) {
    case '': // if mapping type is not defined, it's probably just a category node _around_ other mapping nodes. e.g. Outputs
    case InputMappingConstants.MAPPING_BUILD:
    case InputMappingConstants.MAPPING_ARRAY:
    case InputMappingConstants.MAPPING_ARRAY_CONCAT:
    case InputMappingConstants.MAPPING_RUN_CONVERTER_FLOW:
    case InputMappingConstants.MAPPING_DATETIME_COMPUTE:
    case OutputMappingConstants.MAPPING_EXPOSE_PROPERTIES:
    case OutputMappingConstants.MAPPING_ARRAY_FIRST_ITEM:
    case OutputMappingConstants.MAPPING_ARRAY_LAST_ITEM:
    case OutputMappingConstants.MAPPING_RUN_CONVERTER_FLOW:
      return true;
    default:
      return false;
  }
}

export function mappingTypeHasConnectors(mappingType: string) {
  switch (mappingType) {
    case InputMappingConstants.MAPPING_SELECT:
    case OutputMappingConstants.MAPPING_CHANGE:
      return true;
    default:
      return false;
  }
}

export function getNestedDataDescMap(
  dataDescriptions: DataDescription[],
  path: string = '',
  filterType: MappingPanelFilter = MappingPanelFilter.ALL,
  mappedNodes: string[] = []
) {
  // ES6 maps preserve order, and so far these are pre-sorted on the server.
  const map = new Map<string, NestedDataDesc>();
  dataDescriptions.forEach(dd => {
    const childPath = `${path}${DATA_PATH_SEPARATOR}${dd.Name}`;
    const filtered = !getMappedFilterMatch(childPath, filterType, mappedNodes);
    const nested: NestedDataDesc = { ...dd, filtered, children: new Map() };

    map.set(nested.Name, nested);
  });
  return map;
}

export function getVisibleFlowDataNodes(
  flowData: Map<string, NestedDataDesc>,
  expandedNodes: Map<string, boolean>
): string[] {
  if (!flowData.size) {
    return [];
  }
  let names: string[] = [];
  flowData.forEach(desc => {
    if (!desc.filtered) {
      names.push(desc.Name);
      getVisibleChildNames(desc, names, desc.Name, expandedNodes);
    }
  });

  return names;
}

function getVisibleChildNames(
  desc: NestedDataDesc,
  names: string[],
  path: string,
  expandedNodes: Map<string, boolean>
) {
  if (expandedNodes.get(path)) {
    desc.children.forEach(child => {
      if (!child.filtered) {
        const childPath = `${path}${DATA_PATH_SEPARATOR}${child.Name}`;
        names.push(childPath);
        getVisibleChildNames(child, names, childPath, expandedNodes);
      }
    });
  }
  return names;
}

export function getStepNodeData(
  baseNodes: ContextProviderNode[],
  filtered: ContextProviderNode[],
  expandedNodes: Map<string, boolean>
) {
  const mappedDataPaths: string[] = [];
  const allStepNodes = getStepDataNodes(baseNodes, filtered, expandedNodes, mappedDataPaths);
  const visibleStepNodes = getVisibleStepNodes(allStepNodes);
  return { allStepNodes, visibleStepNodes, mappedDataPaths };
}

export function getStepDataNodes(
  baseNodes: ContextProviderNode[],
  filtered: ContextProviderNode[],
  expandedNodes: Map<string, boolean>,
  mappedDataPaths: string[]
) {
  const allNodes: StepDataMappingNode[] = [];

  baseNodes.forEach(node => {
    addStepDataNode(allNodes, mappedDataPaths, filtered, node, '', true, expandedNodes);
  });

  return allNodes;
}

/**
 * A flat list of all node paths, for use in indexing mapping connections later.
 *
 * categories that cannot be mapped will be prefixed "Category:" so they are skipped in the reverse lookup from Y coordinates
 * when dragging a new connector line.
 *
 * @param allNodes to filter for visibility
 * @returns visible node list
 */
function getVisibleStepNodes(allNodes: StepDataMappingNode[] = []) {
  return allNodes
    .filter(entry => entry.visible && !entry.filtered)
    .map(entry => (entry.isMappingNode ? entry.nodeName : `${MAPPING_CATEGORY_PREFIX}${entry.nodeName}`));
}

export function getMappedValueNode(node: ContextProviderNode) {
  // in a mapping category, the first child is mapping type, and second is the value:
  return node.Children?.[1];
}

export function getMappedDataPath(node: ContextProviderNode, mappingType = '') {
  return mappingTypeHasConnectors(mappingType) ? (getMappedValueNode(node)?.Value?.OutputValue as string) || '' : '';
}

export function getCanHaveChildMappings(node: ContextProviderNode, mapping = '') {
  return isContextCategoryNode(node.Node) && mappingTypeHasChildren(mapping);
}

export function getNodeIsInitiallyOpen(node: ContextProviderNode, mapping = '') {
  return getCanHaveChildMappings(node, mapping) && !(node.Node as ContextCategoryNode).InitiallyCollapsed;
}

/** mapping editor search also compares data type display name */
export function searchMatchesDataType(node: ContextProviderNode, search = '') {
  return getMappingDataType(node, true)
    .toUpperCase()
    .includes(search.toUpperCase());
}

/**
 * evaluate whether a row / connector line is hovered or not.
 *
 * (object arg would be more elegant here, but since this fires on hover, let's not tax the garbage collector.)
 *
 * @param mappings to look across colums with
 * @param hoveredFlowNode currently hovered flow node
 * @param hoveredStepNode currently hovered step node
 * @param path to compare
 * @param forFlow false to compare via step nodes
 * @returns true if provided path is directly or indirectly hovered
 */
export function isRowHovered(
  mappings: IMapping[],
  hoveredFlowNode: string,
  hoveredStepNode: string,
  path: string,
  forFlow = true
): boolean {
  return (
    isRowDirectlyHovered(hoveredFlowNode, hoveredStepNode, path, forFlow) ||
    isRowIndirectlyHovered(mappings, hoveredFlowNode, hoveredStepNode, path, forFlow)
  );
}

export function isRowIndirectlyHovered(
  mappings: IMapping[],
  hoveredFlowNode: string,
  hoveredStepNode: string,
  path: string,
  forFlow = true
): boolean {
  if ((!hoveredFlowNode && !hoveredStepNode) || !path) {
    return false;
  }

  if (!forFlow) {
    if (hoveredFlowNode) {
      return !!(mappings.find(mapping => mapping.nodeName === path)?.dataPath === hoveredFlowNode);
    }
  }

  if (forFlow) {
    if (hoveredStepNode) {
      return !!(mappings.find(mapping => mapping.dataPath === path)?.nodeName === hoveredStepNode);
    }
  }
  return false;
}

export function isRowDirectlyHovered(
  hoveredFlowNode: string,
  hoveredStepNode: string,
  path: string,
  forFlow = true
): boolean {
  if ((!hoveredFlowNode && !hoveredStepNode) || !path) {
    return false;
  }

  if (!forFlow) {
    return path === hoveredStepNode;
  }

  if (forFlow) {
    return path === hoveredFlowNode;
  }
  return false;
}

export function isAnyLineHovered(mappings: IMapping[], hoveredFlowNode: string, hoveredStepNode: string): boolean {
  if (hoveredFlowNode) {
    return !!mappings.find(mapping => mapping.dataPath === hoveredFlowNode);
  }
  if (hoveredStepNode) {
    return !!mappings.find(mapping => mapping.nodeName === hoveredStepNode && mapping.dataPath);
  }
  return false;
}

export interface StepDataMappingNode {
  nodeName: string;
  dataPath: string;
  mappingName: string;
  visible: boolean;
  // interestingly, immer will freeze this `originalNode` too if we pass it via mappings, which causes errors in
  // contextProviderNodeReducer. We'll work around this until we have time to immer-ify that reducer, too.
  // This was only actually used for validations
  // originalNode: ContextProviderNode;
  validation: NodeValidationIssues;
  isMappingNode: boolean;
  filtered?: boolean;
}

function addStepDataNode(
  allNodes: StepDataMappingNode[],
  mappedDataPaths: string[],
  filtered: ContextProviderNode[],
  currentNode: ContextProviderNode,
  parentNodeName: string,
  visible: boolean,
  expandedNodes: Map<string, boolean>
) {
  let mappingType = getMappingName(currentNode);
  let nodeName =
    parentNodeName.length > 0
      ? `${parentNodeName}${PROVIDER_NODE_PATH_SEPARATOR}${currentNode.Node.Name}`
      : currentNode.Node.Name;
  const found = findContextChildInListByPath(nodeName.split(PROVIDER_NODE_PATH_SEPARATOR), filtered);
  const dataPath = getMappedDataPath(currentNode, mappingType);
  const validationIssues = getAllValidationIssues(currentNode);
  allNodes.push({
    nodeName,
    dataPath,
    mappingName: mappingType,
    visible,
    validation: validationIssues,
    isMappingNode: categoryIsMappingTypeSelector(currentNode),
    filtered: !found,
  });

  if (dataPath && !validationIssues?.issues?.length) mappedDataPaths.push(dataPath);

  // let isCategory = isContextCategoryNode(currentNode.Node);
  let canHaveChildMappings = getCanHaveChildMappings(currentNode, mappingType);
  let childrenVisible =
    visible && // if parent says it's not visible, it's not visible.
    (expandedNodes.get(nodeName) !== undefined
      ? !!expandedNodes.get(nodeName) // if it's been expanded or collapsed by the user, take that,
      : getNodeIsInitiallyOpen(currentNode, mappingType)); //otherwise the default from the server:

  // we're iterating all child mapping selectors, because we'll need to draw lines to parents of hidden children, too
  if (canHaveChildMappings) {
    for (let i = getIndexAsPerContextNode(currentNode, mappingType); i < currentNode.Children.length; i++) {
      if (isSpecialMappingNode(currentNode.Children[i])) {
        continue;
      }
      addStepDataNode(
        allNodes,
        mappedDataPaths,
        filtered,
        currentNode.Children[i],
        nodeName,
        childrenVisible,
        expandedNodes
      );
    }
  }
}

function getIndexAsPerContextNode(node: ContextProviderNode, mappingType: string) {
  return categoryIsMappingTypeSelector(node) && containsPropertyNode(node, mappingType)
    ? 2
    : categoryIsMappingTypeSelector(node) // if it's a mapping node start with 1 because 0 contains mapping type, if it's _just_ a category, then 0.
    ? 1
    : 0;
}

function isSpecialMappingNode(node: ContextProviderNode) {
  return !!MappingSuffixes.find(suffix => {
    return node.Node.Name.includes(suffix);
  });
}

export function containsPropertyNode(node: ContextProviderNode, mapping: string) {
  if (
    mapping === InputMappingConstants.MAPPING_RUN_CONVERTER_FLOW ||
    mapping === OutputMappingConstants.MAPPING_RUN_CONVERTER_FLOW ||
    mapping === InputMappingConstants.MAPPING_DATETIME_COMPUTE
  ) {
    return node && node.Children?.length >= 1 && node.Children[1].Node.__type === NodeTypes.Property;
  }
}

export function getMappingValidationId(path: string, validation: ValidationSummary) {
  if (validation.worstLevel !== BreakLevel.Fatal) {
    return {};
  }

  const validationTooltipId = `${getMappingElementId(path)}-validation`;
  return {
    validationTooltipId,
    validationAttributes: { 'aria-describedby': validationTooltipId },
  };
}

export function getMappingElementId(path: string) {
  return `mapping-${path.replaceAll(' ', '_')}`;
}

export function getDataElementId(path: string) {
  return `data-${path.replaceAll(' ', '_')}`;
}

/**
 * This is debounced so we don't spam the store on things which update many rows at once.
 */
export const updateMappingRowHeights = debounce(
  (id: string, update: (map: IMappingRowDimensions) => void, className = 'dp-step-mapping-row') => {
    const root = document.getElementById(id);
    if (!root) {
      return;
    }

    const map: IMappingRowDimensions = new Map<string, { top: number; height: number }>();
    Array.from(root.getElementsByClassName(className)).forEach(el => {
      map.set(el.id, { top: (el as HTMLElement).offsetTop, height: (el as HTMLElement).offsetHeight });
    });

    console.log('!!!updateMappingRowHeights!!!');
    update(map);
  },
  17 // if they're coming in less than 60fps, just wait that long - unless this is over-engineered, because React is going to use RAF?
);

/*
  if (!updatingRows) {
    // request animation frame to limit how often this fires:
    window.requestAnimationFrame(() => {
      updatingRows = false;
      updateRows()
    });

    updatingRows = true;
    return;
  }
*/

/**
 * This is derivative of PropertyGrid search, but it's necessarily different in that we are matching mapping type names,
 * and we are matching categories & MappingTypeSelectors without further filtering non-category children.
 *
 * It's necessary _not_ to further filter children, because other child nodes (like mapping values) _don't_ need to be matched in search, and can't be excluded by search
 * without breaking things.
 *
 * @param search search string
 * @param nodes
 */
export function stepNodeSearch(
  search: string,
  nodes: ContextProviderNode[],
  setExpandedNodes: (nodes: Map<string, boolean>) => void,
  andConditions: IProviderNodeSearchCondition[] = []
) {
  const expandedBySearch = new Map<string, boolean>();
  const expansionTracker = (name: string, expanded: boolean) => {
    expandedBySearch.set(name, expanded);
  };
  const filteredNodes = providerNodeSearch(
    search,
    nodes,
    andConditions,
    [searchMatchesDataType],
    false,
    true,
    expansionTracker
  );
  setExpandedNodes(expandedBySearch);
  return filteredNodes;
}

export function createConditionPerFilter(filter: MappingPanelFilter) {
  return filter === MappingPanelFilter.ALL
    ? () => true
    : (node: ContextProviderNode) => {
        const hasNoValidation = getParentValidationSummary(node).reasons.length <= 0;
        const mappingName = getMappingName(node);
        if (isConverterWithNoInput(mappingName, node)) {
          return (
            (filter === MappingPanelFilter.MAPPED && hasNoValidation) ||
            (filter === MappingPanelFilter.UNMAPPED && !hasNoValidation)
          );
        }
        if (mappingName === '' || getCanHaveChildMappings(node, mappingName)) {
          // this node shouldn't be evaluated on whether it, itself, is mapped or not,
          // so either the rest of the recursive search will add it for it's children or not.
          return false;
        }

        const isMapped =
          ((mappingName === InputMappingConstants.MAPPING_SELECT ||
            mappingName === OutputMappingConstants.MAPPING_CHANGE ||
            mappingName === InputMappingConstants.MAPPING_DATETIME_CURRENT) &&
            hasNoValidation) ||
          editorHasValidValue(node, mappingName);

        return (
          (filter === MappingPanelFilter.UNMAPPED && !isMapped) || (filter === MappingPanelFilter.MAPPED && isMapped)
        );
      };
}

export function filterFlowDataNodes(
  flowData: Map<string, NestedDataDesc>,
  newExpandedNodes: Map<string, boolean>,
  searchString: string,
  filterType: MappingPanelFilter,
  mappedNodes: string[]
) {
  // Starting with all mapped nodes where the path is valid.

  return getFlowDataNodeFilterResults(flowData, newExpandedNodes, [], searchString, filterType, mappedNodes);
}

function getFlowDataNodeFilterResults(
  flowData: Map<string, NestedDataDesc>,
  newExpandedNodes: Map<string, boolean>,
  filteredNodes: string[],
  searchString: string,
  filterType: MappingPanelFilter,
  mappedNodes: string[],
  skipSearch: boolean = false,
  parentPath: string = ''
) {
  const results = new Map<string, NestedDataDesc>();

  flowData.forEach((dataDesc, name) => {
    //search and filter results are handle separately to ensure child nodes of search match will show
    let searchMatch = true;
    let filterMatch = true;
    let filter = false;
    let children = new Map<string, NestedDataDesc>();
    let filterPath = !parentPath ? name : `${parentPath}${DATA_PATH_SEPARATOR}${name}`;
    filterMatch = getMappedFilterMatch(filterPath, filterType, mappedNodes);
    if (!skipSearch) {
      searchMatch = getFlowNodeSearchMatch(dataDesc, searchString);
    }

    filter = !(searchMatch && filterMatch);

    if (dataDesc.children.size > 0) {
      ({ results: children, newExpandedNodes, filteredNodes } = getFlowDataNodeFilterResults(
        dataDesc.children,
        newExpandedNodes,
        filteredNodes,
        searchString,
        filterType,
        mappedNodes,
        searchMatch,
        filterPath
      ));

      //If there are children results, then this node should not be filtered. And it should be expanded.
      if (Array.from(children.values()).filter(entry => !entry.filtered).length > 0) {
        if (searchString) {
          //only expand if searching
          newExpandedNodes.set(filterPath, true);
          filter = false;
        } else if (filterMatch === false && filterType === MappingPanelFilter.UNMAPPED) {
          // only filtering, only show mapped parent with unmapped children, e.g. if it was already expanded.
          filter = newExpandedNodes.get(filterPath) ? false : true;
        }
      }
    }
    if (filter) {
      filteredNodes.push(filterPath);
    }

    var newValue = { ...dataDesc, filtered: filter, children };
    results.set(name, newValue);
  });

  return { results, newExpandedNodes, filteredNodes };
}

function getFlowNodeSearchMatch(dd: DataDescription, searchString: string) {
  return (
    !searchString ||
    dd.Name.toUpperCase().includes(searchString.toUpperCase()) ||
    dd.FormattedTypeName.toUpperCase().includes(searchString.toUpperCase())
  );
}

function getMappedFilterMatch(path: string, filterType: MappingPanelFilter, mappedNodes: string[]) {
  let isMappedNode = mappedNodes.includes(path);
  switch (filterType) {
    case MappingPanelFilter.ALL:
      return true;
    case MappingPanelFilter.MAPPED:
      if (isMappedNode) return true;
      //check if child is mapped
      path = `${path}${DATA_PATH_SEPARATOR}`;
      isMappedNode = mappedNodes.some(mappedNode => mappedNode.includes(path));
      return isMappedNode;

    case MappingPanelFilter.UNMAPPED:
      return !isMappedNode;
  }
}

export function getConverterFlowSuggestionActions(converters: string[]): IActionLinkItems | undefined {
  const suggestedConverters = converters?.filter(value =>
    value.includes(`Suggested Converters${InputMappingConstants.CONVERTER_FLOW_CATEGORY_SEPARATOR}`)
  );
  if (!suggestedConverters?.length) return undefined;

  let actionLinks: IActionLinkItem[] = [];
  suggestedConverters.forEach(value => {
    value = value.slice(value.lastIndexOf('|') + 1);
    const label = value.slice(value.lastIndexOf('+') + 1);
    const entry = { label, value, classname: 'dp-converter-suggestion__item' };
    actionLinks.push(entry);
  });
  return { current: actionLinks };
}

function mergeTextEditorContainsAnyValue(value: string) {
  //in case of MergeTextEditor, if you click OK without adding anything we are getting output value like "<html><head></head><body></body></html>"
  return value?.substring(value.indexOf('<body>') + 6, value.indexOf('</body>')).length > 0;
}
function editorHasValidValue(node: ContextProviderNode, mappingType: string) {
  if (mappingTypeRendersInlineEditor(mappingType)) {
    const mappedNode = getMappedValueNode(node) as IPropertyChild;
    if (mappedNode.Node.MvcEditorName === EditorNames.CheckBox) return true;
    if (mappedNode.Node.MvcEditorName === EditorNames.MergeTextEditor)
      return mergeTextEditorContainsAnyValue(mappedNode.Value?.OutputValue);
    if (mappedNode.Value?.OutputValue) return true;
  }
  return false;
}

function isConverterWithNoInput(mappingName: string, node: ContextProviderNode) {
  return (
    (mappingName === InputMappingConstants.MAPPING_RUN_CONVERTER_FLOW ||
      mappingName === OutputMappingConstants.MAPPING_RUN_CONVERTER_FLOW) &&
    node?.Children?.length === 2 //If converter flow is not taking any input in that case we will get only 2 childrens
  );
}

export function showIconOnDataDescriptionRow(dataDescType: DataDescriptionType, data: DataDescription) {
  return (
    (dataDescType === DataDescriptionType.Read && data.HasReadableSubElements) ||
    (dataDescType === DataDescriptionType.Write && data.HasWritableSubElements)
  );
}
